/**
 * PortalAcceptInvitationPage — landing page for invitation email links.
 *
 * Route: /portal/accept-invitation?token={acceptance_token}
 *   Also: /ext/:token/setup (acceptance_token in path, Phase 10d.4 Revision 2)
 *
 * Flow:
 *   1. User clicks invitation email link → arrives here with token in path or ?token=...
 *   2. Fetch tenant theme from GET /api/ext/invitation/{token}/meta (no auth)
 *   3. Inject CSS variables (FOUC prevention — skeleton held until theme ready)
 *   4. If not authenticated → show branded login/signup form
 *   5. After auth → auto-call POST /api/external-views/members/accept-invitation
 *   6. On success → redirect to /ext/{view_token}
 *
 * planTEK Wave 1 — Phase 10d.4: Branded portal entry experience.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase.ts';
import { useAuth } from '@/lib/auth/AuthProvider.tsx';
import { api } from '@/lib/api';
import { injectCSSVariables, DEFAULT_THEME } from '@/lib/theme';
import type { TenantTheme } from '@/lib/theme';
import { PoweredByFooter } from '@/components/external/PoweredByFooter';

type PageMode = 'login' | 'signup' | 'forgot';

function getFirebaseErrorMessage(message: string): string {
  if (message.includes('user-not-found') || message.includes('wrong-password') || message.includes('invalid-credential')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (message.includes('email-already-in-use')) {
    return 'An account with this email already exists. Sign in instead.';
  }
  if (message.includes('weak-password')) {
    return 'Password must be at least 6 characters.';
  }
  if (message.includes('invalid-email')) {
    return 'Please enter a valid email address.';
  }
  if (message.includes('too-many-requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  return message;
}

export function PortalAcceptInvitationPage() {
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, signIn, signInWithEmail, createUserWithEmail, clearError } = useAuth();

  // Support /ext/:token/setup (path param) and /portal/accept-invitation?token=... (query param)
  const invitationToken = pathToken ?? searchParams.get('token') ?? '';

  const [themeReady, setThemeReady] = useState(false);
  const [branding, setBranding] = useState<TenantTheme['branding'] | undefined>();
  const [mode, setMode] = useState<PageMode>('login');
  const [authTab, setAuthTab] = useState<'email' | 'google'>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  // Fetch tenant theme from invitation token before revealing the page (FOUC prevention).
  // Falls back to DEFAULT_THEME if the token is invalid or tenant has no custom theme.
  useEffect(() => {
    if (!invitationToken) {
      injectCSSVariables(DEFAULT_THEME);
      setThemeReady(true);
      return;
    }
    api.externalViews.getInvitationMeta(invitationToken)
      .then((meta) => {
        if (meta?.theme) {
          injectCSSVariables(meta.theme);
          setBranding(meta.theme?.branding ?? undefined);
        } else {
          injectCSSVariables(DEFAULT_THEME);
        }
      })
      .catch(() => {
        injectCSSVariables(DEFAULT_THEME);
      })
      .finally(() => {
        setThemeReady(true);
      });
  }, [invitationToken]);

  // Once authenticated, auto-accept the invitation
  useEffect(() => {
    if (!isAuthenticated || !invitationToken || accepting) return;

    let cancelled = false;
    setAccepting(true);
    setError(null);

    api.externalViews.acceptInvitation(invitationToken)
      .then((result) => {
        if (cancelled) return;
        const viewToken = result?.view_token;
        if (viewToken) {
          navigate(`/ext/${viewToken}`, { replace: true });
        } else {
          // No view token → go to my-views
          navigate('/my-views', { replace: true });
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        const detail = err?.data?.detail || err?.message || 'Failed to accept invitation.';
        if (detail.includes('already active') || detail.includes('already accepted')) {
          // Already accepted — go to my-views
          navigate('/my-views', { replace: true });
        } else {
          setError(detail);
          setAccepting(false);
        }
      });

    return () => { cancelled = true; };
  }, [isAuthenticated, invitationToken, navigate, accepting]);

  const clearErrors = useCallback(() => {
    setError(null);
    clearError();
  }, [clearError]);

  const handleGoogleSignIn = useCallback(async () => {
    clearErrors();
    try {
      await signIn();
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [signIn, clearErrors]);

  const handleEmailSignIn = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    try {
      await signInWithEmail(email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [email, password, signInWithEmail, clearErrors]);

  const handleSignUp = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    try {
      await createUserWithEmail(email, password);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    }
  }, [email, password, confirmPassword, createUserWithEmail, clearErrors]);

  const handleForgotPassword = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!email) {
      setError('Please enter your email address.');
      return;
    }
    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err.message));
    } finally {
      setResetLoading(false);
    }
  }, [email, clearErrors]);

  const switchMode = useCallback((next: PageMode) => {
    setMode(next);
    setError(null);
    setResetSent(false);
  }, []);

  const logoUrl = branding?.logo_url;
  const companyName = branding?.company_name;

  // Hold skeleton until theme is injected (FOUC prevention)
  if (!themeReady) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        data-testid="invitation-loading"
        style={{ backgroundColor: 'var(--background, #f9fafb)' }}
      >
        <div className="w-full max-w-sm space-y-4 animate-pulse">
          <div className="h-14 w-14 rounded-2xl mx-auto" style={{ backgroundColor: 'var(--muted, #e5e7eb)' }} />
          <div className="h-5 w-48 rounded mx-auto" style={{ backgroundColor: 'var(--muted, #e5e7eb)' }} />
          <div className="h-48 w-full rounded-2xl" style={{ backgroundColor: 'var(--muted, #e5e7eb)' }} />
        </div>
      </div>
    );
  }

  // Missing token guard
  if (!invitationToken) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: 'var(--background, #f9fafb)' }}
      >
        <div className="max-w-sm w-full text-center">
          <div
            className="rounded-2xl border shadow-sm p-8 space-y-4"
            style={{
              backgroundColor: 'var(--card, var(--background))',
              borderColor: 'var(--border, #e5e7eb)',
            }}
          >
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto"
              style={{ backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 15%, transparent)' }}
            >
              <svg className="w-6 h-6" style={{ color: 'var(--destructive, #dc2626)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Invalid invitation link</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
              This link appears to be incomplete or expired. Please check your email for the original invitation.
            </p>
          </div>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Loading: auth initializing or accepting invitation
  if (authLoading || (isAuthenticated && accepting)) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: 'var(--background, #f9fafb)' }}
      >
        <div className="text-center space-y-4">
          <div
            className="inline-block w-8 h-8 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}
          />
          <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
            {isAuthenticated ? 'Activating your account...' : 'Loading...'}
          </p>
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  // Acceptance error (shown when authenticated but accept call failed)
  if (isAuthenticated && error) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: 'var(--background, #f9fafb)' }}
      >
        <div className="max-w-sm w-full">
          <div
            className="rounded-2xl border shadow-sm p-8 space-y-4 text-center"
            style={{
              backgroundColor: 'var(--card, var(--background))',
              borderColor: 'var(--border, #e5e7eb)',
            }}
          >
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full mx-auto"
              style={{ backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 15%, transparent)' }}
            >
              <svg className="w-6 h-6" style={{ color: 'var(--destructive, #dc2626)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h1 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Could not activate account</h1>
            <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>{error}</p>
            <button
              onClick={() => navigate('/my-views')}
              className="text-sm font-medium hover:underline focus:outline-none"
              style={{ color: 'var(--primary)' }}
            >
              Go to my views
            </button>
          </div>
          <PoweredByFooter />
        </div>
      </div>
    );
  }

  // Not authenticated → show branded login/signup
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      data-testid="invitation-login"
      style={{ backgroundColor: 'var(--background, #f9fafb)' }}
    >
      <div className="w-full max-w-sm">
        {/* Tenant branding header */}
        <div className="text-center mb-8">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={companyName || 'Company logo'}
              className="h-12 max-w-[200px] mx-auto mb-4 object-contain"
              data-testid="tenant-logo"
            />
          ) : (
            <div
              className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-4 text-2xl font-bold select-none"
              style={{
                backgroundColor: 'var(--primary)',
                color: 'var(--primary-foreground, #fff)',
              }}
              data-testid="tenant-monogram"
            >
              {companyName ? companyName.charAt(0).toUpperCase() : 'P'}
            </div>
          )}

          {companyName && (
            <p
              className="text-sm font-semibold mb-1 truncate"
              style={{ color: 'var(--foreground)' }}
              data-testid="tenant-company-name"
            >
              {companyName}
            </p>
          )}

          <h1
            className="text-xl font-bold"
            style={{ color: 'var(--foreground)' }}
          >
            {mode === 'forgot'
              ? 'Reset your password'
              : mode === 'signup'
              ? 'Set up your account'
              : "You've been invited"}
          </h1>
          {mode === 'login' && (
            <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
              Sign in to activate your access
            </p>
          )}
        </div>

        {/* Card */}
        <div
          className="rounded-2xl border shadow-sm p-6"
          style={{
            backgroundColor: 'var(--card, var(--background))',
            borderColor: 'var(--border, #e5e7eb)',
          }}
        >
          {mode === 'forgot' ? (
            /* Forgot password form */
            <form className="space-y-4" onSubmit={handleForgotPassword}>
              <p className="text-sm" style={{ color: 'var(--muted-foreground, #6b7280)' }}>
                Enter your email and we'll send you a reset link.
              </p>
              {error && (
                <div
                  className="p-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 10%, transparent)',
                    color: 'var(--destructive, #b91c1c)',
                    border: '1px solid color-mix(in srgb, var(--destructive, #ef4444) 30%, transparent)',
                  }}
                  role="alert"
                >
                  {error}
                </div>
              )}
              {resetSent && (
                <div
                  className="p-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'color-mix(in srgb, #22c55e 10%, transparent)',
                    color: '#15803d',
                    border: '1px solid color-mix(in srgb, #22c55e 30%, transparent)',
                  }}
                >
                  Reset link sent! Check your inbox.
                </div>
              )}
              <ThemedFormField
                id="accept-forgot-email"
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                required
              />
              <ThemedButton type="submit" disabled={!email || resetLoading}>
                {resetLoading ? 'Sending...' : 'Send reset link'}
              </ThemedButton>
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { switchMode('login'); }}
                  className="text-sm hover:underline focus:outline-none"
                  style={{ color: 'var(--muted-foreground, #6b7280)' }}
                >
                  Back to sign in
                </button>
              </div>
            </form>
          ) : (
            <>
              {/* Auth tab switcher */}
              <div
                className="flex rounded-lg p-1 mb-5"
                style={{ backgroundColor: 'var(--muted, #f3f4f6)' }}
              >
                {(['email', 'google'] as const).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setAuthTab(tab)}
                    className="flex-1 py-1.5 text-sm font-medium rounded-md transition-colors capitalize"
                    style={{
                      backgroundColor: authTab === tab ? 'var(--background)' : 'transparent',
                      color: authTab === tab ? 'var(--foreground)' : 'var(--muted-foreground, #6b7280)',
                      boxShadow: authTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    }}
                  >
                    {tab === 'google' ? 'Google' : 'Email'}
                  </button>
                ))}
              </div>

              {error && (
                <div
                  className="mb-4 p-3 rounded-lg text-sm"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--destructive, #ef4444) 10%, transparent)',
                    color: 'var(--destructive, #b91c1c)',
                    border: '1px solid color-mix(in srgb, var(--destructive, #ef4444) 30%, transparent)',
                  }}
                  role="alert"
                >
                  {error}
                </div>
              )}

              {authTab === 'google' ? (
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  className="w-full flex items-center justify-center gap-3 rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2"
                  style={{
                    backgroundColor: 'var(--background)',
                    borderColor: 'var(--border, #d1d5db)',
                    color: 'var(--foreground)',
                  }}
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
              ) : mode === 'signup' ? (
                <form className="space-y-4" onSubmit={handleSignUp}>
                  <ThemedFormField id="accept-signup-email" label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required />
                  <ThemedFormField id="accept-signup-password" label="Password" type="password" value={password} onChange={setPassword} placeholder="Min. 8 characters" required />
                  <ThemedFormField id="accept-signup-confirm" label="Confirm password" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Re-enter password" required />
                  <ThemedButton type="submit" disabled={!email || !password || !confirmPassword}>
                    Create account
                  </ThemedButton>
                </form>
              ) : (
                <form className="space-y-4" onSubmit={handleEmailSignIn}>
                  <ThemedFormField id="accept-email" label="Email" type="email" value={email} onChange={setEmail} placeholder="you@example.com" required />
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label
                        htmlFor="accept-password"
                        className="block text-sm font-medium"
                        style={{ color: 'var(--foreground)' }}
                      >
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={() => switchMode('forgot')}
                        className="text-xs font-medium hover:underline focus:outline-none"
                        style={{ color: 'var(--primary)' }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <input
                      id="accept-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="block w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus-visible:ring-2"
                      style={{
                        backgroundColor: 'var(--background)',
                        borderColor: 'var(--border, #d1d5db)',
                        color: 'var(--foreground)',
                      }}
                    />
                  </div>
                  <ThemedButton type="submit" disabled={!email || !password}>
                    Sign in
                  </ThemedButton>
                </form>
              )}

              {/* Mode switcher */}
              <div
                className="mt-4 text-center text-sm"
                style={{ color: 'var(--muted-foreground, #6b7280)' }}
              >
                {mode === 'login' ? (
                  <>
                    First time?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('signup')}
                      className="font-medium hover:underline focus:outline-none"
                      style={{ color: 'var(--primary)' }}
                    >
                      Set up your account
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => switchMode('login')}
                      className="font-medium hover:underline focus:outline-none"
                      style={{ color: 'var(--primary)' }}
                    >
                      Sign in
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <PoweredByFooter />
      </div>
    </div>
  );
}

// ── Themed form primitives (CSS variable-based, not hardcoded colors) ─────────

interface ThemedFormFieldProps {
  id: string;
  label: string;
  type: 'email' | 'password' | 'text';
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}

function ThemedFormField({ id, label, type, value, onChange, placeholder, required }: ThemedFormFieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium mb-1"
        style={{ color: 'var(--foreground)' }}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="block w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus-visible:ring-2"
        style={{
          backgroundColor: 'var(--background)',
          borderColor: 'var(--border, #d1d5db)',
          color: 'var(--foreground)',
        }}
      />
    </div>
  );
}

interface ThemedButtonProps {
  type?: 'submit' | 'button';
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

function ThemedButton({ type = 'button', disabled, onClick, children }: ThemedButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      className="w-full flex items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold transition-opacity focus:outline-none focus-visible:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        backgroundColor: 'var(--primary)',
        color: 'var(--primary-foreground, #fff)',
      }}
    >
      {children}
    </button>
  );
}
